/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving an individual patient.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */
import reducerRegistry from '../ReducerRegistry';

/*
 * Used to get access to the API types and requests
 */
import * as Dtos from '../../api/dtos';

/**
 * Enumeration used to determine the state of a request.
 */
import { IRequestState, RequestState } from '.';

/**
 * Used to create a get request to the servicestack API.
 */
import { createGetRequest } from '../../utilities/createRequest';

/**
 * Typings
 */
import { ILogicSideEffect } from '../../utilities/logicTypes';
import client from '../../api/client';
import { IAuthSession } from '@servicestack/client';

/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/
export enum TaxMode {
    Included = 1,
    Excluded = 2
}


interface IInitialApplicationSettingsState {
    taxMode: TaxMode;
}

export interface IApplicationSettingsState {
    applicationSettings: IInitialApplicationSettingsState;
}

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const ApplicationSettingsInitialState: IInitialApplicationSettingsState = {
    taxMode: TaxMode.Excluded
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useApplicationSettingsSelector: TypedUseSelectorHook<IApplicationSettingsState> = useSelector;

export const ApplicationSettingsSelectors = {
    selectTaxMode: (state: IApplicationSettingsState) => state.applicationSettings.taxMode
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class ApplicationSettingsReducer extends ImmerReducer<IInitialApplicationSettingsState> {
    public setTaxMode(taxMode: TaxMode) {
        this.draftState.taxMode = taxMode;
    }
}

export const ApplicationSettingsActions = createActionCreators(ApplicationSettingsReducer);
const reducer = createReducerFunction(ApplicationSettingsReducer, ApplicationSettingsInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

// export const ApplicationSettingsApi = {
// }

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

// const ApplicationSettingsLogic = {
// };

// const logics = [
// ]

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('applicationSettings', reducer);


// WEBPACK FOOTER //
// ./src/store/modules/applicationSettings.ts